import React, { useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import MUIIcon from '@material-ui/icons/KeyboardArrowDown'
import { useIntl, FormattedMessage } from 'gatsby-plugin-intl'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import { breakpoint } from 'src/theme/grid'
import CheckIcon from 'src/assets/icons/check.svg'
import { useGlobalModal } from 'src/components/GlobalModalProvider'
import AcceptWithTrackingModal from 'src/components/AcceptWithTrackingModal'

const AcceptReservationButton = ({
  className,
  active,
  acceptReservation,
  offerId,
  hasTracking,
  offerReferenceNumber,
  refreshList
}) => {
  const intl = useIntl()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const buttonRef = useRef(null)
  const mobileButtonRef = useRef(null)
  const { open } = useGlobalModal()

  const handleMenuClick = e => {
    e.stopPropagation()
    setMenuOpen(!menuOpen)
  }

  const handleMobileMenuClick = e => {
    e.stopPropagation()
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const handleAcceptReservation = params => {
    acceptReservation(params)
    if (refreshList) refreshList()
    if (params.withTracking) {
      const modalTitle =
        intl.formatMessage(
          { id: 'myOffers.acceptWithTrackingModalTitle' },
          { offerId: offerReferenceNumber }
        ) || `Accept with tracking - ${offerReferenceNumber}`

      const confirmText =
        intl.formatMessage({ id: 'myOffers.completeData' }) || 'Complete data'
      const cancelText =
        intl.formatMessage({ id: 'myOffers.notNow' }) || 'Not now'

      open(AcceptWithTrackingModal, {
        title: modalTitle,
        offerReferenceNumber,
        trackingOfferId: offerId,
        intl,
        confirmText,
        cancelText
      })
    }
  }

  return (
    <>
      <MobileButton
        active={active}
        onClick={handleMobileMenuClick}
        ref={mobileButtonRef}
      >
        <CheckIcon />
        <Menu
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          anchorEl={mobileButtonRef?.current}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
        >
          <MenuItem
            onClick={() => {
              setMobileMenuOpen(false)
              handleAcceptReservation({ withTracking: true })
            }}
          >
            {intl.formatMessage({ id: 'myOffers.acceptWithTracking' })}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMobileMenuOpen(false)
              handleAcceptReservation({ withTracking: false })
            }}
          >
            {intl.formatMessage({ id: 'myOffers.acceptWithoutTracking' })}
          </MenuItem>
        </Menu>
      </MobileButton>
      <div
        className={className}
        onClick={() => handleAcceptReservation({ withTracking: hasTracking })}
      >
        <span>
          <FormattedMessage id='myOffers.accept' />
        </span>
        <ContextMenuButtonButton
          active={active}
          onClick={handleMenuClick}
          ref={buttonRef}
        >
          <ArrowIcon
            style={{
              transform: `rotate(${menuOpen ? '180deg' : '0'})`
            }}
          />
          <Menu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            anchorEl={buttonRef?.current}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
          >
            <MenuItem
              onClick={() => {
                setMenuOpen(false)
                handleAcceptReservation({ withTracking: true })
              }}
            >
              {intl.formatMessage({ id: 'myOffers.acceptWithTracking' })}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuOpen(false)
                handleAcceptReservation({ withTracking: false })
              }}
            >
              {intl.formatMessage({ id: 'myOffers.acceptWithoutTracking' })}
            </MenuItem>
          </Menu>
        </ContextMenuButtonButton>
      </div>
    </>
  )
}

const StyledAcceptButton = styled(AcceptReservationButton)`
  font-size: 1rem;
  font-weight: 600;
  box-sizing: border-box;
  display: none;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 35px;
  overflow: visible !important;
  padding: 0 1.2rem;
  border-radius: 4px;
  border: none;
  color: ${({ theme }) => theme.colors.mercury.hex()};
  background-color: ${({ theme }) => theme.colors.corduroy.hex()};
  pointer-events: none;
  position: relative;
  padding-right: 52px;
  white-space: nowrap;
  ${({ active }) =>
    active &&
    css`
      pointer-events: auto;
      cursor: pointer;
      color: white;
      background-color: ${({ theme }) => theme.colors.apple.hex()};
      box-shadow: 0 2px 10px 0 rgba(71, 170, 66, 0.25);
    `}
  ${breakpoint.m`
    display: flex;
  `}
`

export default StyledAcceptButton

const ArrowIcon = styled(MUIIcon)`
  color: white !important;
  font-size: 1.2rem;
`

const ContextMenuButtonButton = styled.div`
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.corduroy.darken(0.3).hex()};
  position: absolute;
  right: 0;
  border-bottom-right-radius: 4px;
  border-top-right-radius: 4px;
  cursor: pointer;
  ${({ active }) =>
    active &&
    css`
      background-color: ${({ theme }) => theme.colors.apple.darken(0.3).hex()};
    `}
`

const MobileButton = styled.div`
  box-sizing: border-box;
  padding: 0 0.5rem;
  border-radius: 4px;
  transition: all 0.3s;
  cursor: pointer;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, active }) =>
    active ? theme.colors.apple.hex() : theme.colors.corduroy.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  box-shadow: ${({ active }) =>
    active ? '0 2px 10px 0 rgba(71,170,66,0.25)' : 'none'};
  pointer-events: ${({ active }) => (active ? 'auto' : 'none')};
  opacity: ${({ active }) => (active ? 1 : 0.7)};

  & svg {
    fill: currentColor;
  }

  ${breakpoint.m`
    display: none;
  `}
`
